// ============================================================
// AudienceControl.tsx
// The "Assign to" audience control for TASKS and CASES (multi-assignee
// batch) — lifted from the proven APPOINTMENT modal (AppointmentFormModal's
// Just me / Specific people / Everyone segmented control + firm-member
// picker + attendee chips) so all three surfaces look and behave the same.
//
//   • Just me          → no extra people, not firm-wide (the existing single
//                        owner is untouched).
//   • Specific people  → a firm-member multi-picker (active members only).
//                        Picking it seeds the current user (creator auto-
//                        included, like appointments).
//   • Everyone         → firm-wide.
//
// GATING (UX only — the backend is the real, diff-gated gate):
//   "Specific people" shows only with canPeople; "Everyone" only with canFirm.
//   With neither, the control renders a READ-ONLY summary (AssigneeChips) so
//   a non-granted user still sees who else is on it but can't change it.
// ============================================================
import React from 'react'
import UserAvatar from './UserAvatar'
import type { FirmUser } from '../../types/user.types'

export type Audience = 'me' | 'people' | 'firm'

// Derive the control's mode from stored fields (same rule as the appointment
// modal's seed: firmWide wins; else any assignee rows → people; else me).
export const audienceFrom = (firmWide: boolean | undefined, assigneeIds: string[]): Audience =>
  firmWide ? 'firm' : assigneeIds.length > 0 ? 'people' : 'me'

// The payload fragment for a save — ONLY the fields the user may change, so a
// non-granted user's other edits never trip the backend's diff-gate. Mirrors
// AppointmentFormModal's `...(canCreateFirmWide ? {firmWide} : {})` spread.
export const audiencePayload = (
  audience: Audience,
  selectedIds: string[],
  canPeople: boolean,
  canFirm: boolean,
): { firmWide?: boolean; assigneeUserIds?: string[] } => ({
  ...(canFirm ? { firmWide: audience === 'firm' } : {}),
  ...(canPeople ? { assigneeUserIds: audience === 'people' ? selectedIds : [] } : {}),
})

const IcCheck: React.FC = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" className="w-3 h-3" aria-hidden="true"><path d="M20 6L9 17l-5-5" /></svg>
)
const IcGlobe: React.FC<{ className?: string }> = ({ className = 'w-4 h-4' }) => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className={className} aria-hidden="true">
    <circle cx="12" cy="12" r="9" /><path d="M3 12h18M12 3a15 15 0 0 1 0 18M12 3a15 15 0 0 0 0 18" />
  </svg>
)

const nameOf = (u: FirmUser | undefined): string =>
  u ? (`${u.firstName ?? ''} ${u.lastName ?? ''}`.trim() || u.email || 'Unknown') : 'Former member'

const lblCls = 'block text-[11px] font-semibold uppercase tracking-wide text-casevo-mid mb-1.5'

// ── Read-only chips (view mode + non-granted edit) ──────────────
// Same visual as the appointment's compact attendee chips.
export interface AssigneeChipsProps {
  firmWide?: boolean
  userIds: string[]
  firmUsers: FirmUser[]
  currentUserId: string
  /** Shown when there's nobody extra and it isn't firm-wide. */
  emptyText?: string
}
export const AssigneeChips: React.FC<AssigneeChipsProps> = ({ firmWide, userIds, firmUsers, currentUserId, emptyText = 'Just the owner.' }) => {
  if (firmWide) {
    return (
      <div className="inline-flex items-center gap-2.5 border border-casevo-border dark:bg-[#1f2e26] rounded-lg px-3 py-2 text-sm text-casevo-forest">
        <IcGlobe className="w-4 h-4 text-casevo-brand" />Everyone in the firm
      </div>
    )
  }
  if (userIds.length === 0) return <div className="text-sm text-casevo-mid">{emptyText}</div>
  const byId = new Map(firmUsers.map((u) => [u.id, u]))
  return (
    <div className="flex flex-wrap gap-2">
      {userIds.map((id) => {
        const u = byId.get(id)
        return (
          <span key={id} className="inline-flex items-center gap-2 border border-casevo-border dark:bg-[#1f2e26] rounded-full pl-1 pr-3 py-1">
            {/* Shared UserAvatar (a removed member falls back to initials). */}
            <UserAvatar userId={id} fallbackName={u ? (`${u.firstName} ${u.lastName}`.trim() || u.email) : 'Former member'} size="xs" title={false} />
            <span className="text-[13px] font-medium text-casevo-forest">
              {nameOf(u)}{id === currentUserId ? ' (you)' : ''}
            </span>
          </span>
        )
      })}
    </div>
  )
}

// ── The editable control ────────────────────────────────────────
export interface AudienceControlProps {
  label?: string
  audience: Audience
  onAudience: (a: Audience) => void
  selectedIds: string[]
  onSelectedIds: (ids: string[]) => void
  canPeople: boolean
  canFirm: boolean
  firmUsers: FirmUser[]
  currentUserId: string
  /** 'task' | 'case' — only used for the helper copy. */
  noun: 'task' | 'case'
  /** Everything read-only (e.g. a worker on someone else's task). */
  disabled?: boolean
  /** Hide the built-in label when a parent (e.g. FormField) supplies one. */
  hideLabel?: boolean
}
const AudienceControl: React.FC<AudienceControlProps> = ({
  label = 'Assign to', audience, onAudience, selectedIds, onSelectedIds,
  canPeople, canFirm, firmUsers, currentUserId, noun, disabled = false, hideLabel = false,
}) => {
  // Not permitted to change the audience → read-only summary (never a control
  // that would 403). Same "hide what you can't use" rule as appointments.
  if ((!canPeople && !canFirm) || disabled) {
    return (
      <div>
        {!hideLabel && <label className={lblCls}>{label}</label>}
        <AssigneeChips firmWide={audience === 'firm'} userIds={audience === 'people' ? selectedIds : []}
          firmUsers={firmUsers} currentUserId={currentUserId} emptyText="Just the owner." />
      </div>
    )
  }
  const activeUsers = firmUsers.filter((u) => u.isActive)
  const options = ([
    { key: 'me' as const, label: 'Just me', show: true },
    { key: 'people' as const, label: 'Specific people', show: canPeople },
    { key: 'firm' as const, label: 'Everyone', show: canFirm },
  ]).filter((o) => o.show)
  return (
    <div>
      {!hideLabel && <label className={lblCls}>{label}</label>}
      <div className="flex rounded-lg border border-casevo-border overflow-hidden" role="group" aria-label={`${noun === 'task' ? 'Task' : 'Case'} audience`}>
        {options.map((o, i) => (
          <button key={o.key} type="button" aria-pressed={audience === o.key}
            onClick={() => {
              onAudience(o.key)
              // Creator auto-included (mirrors appointments): picking "Specific
              // people" with nobody chosen yet seeds the current user.
              if (o.key === 'people' && currentUserId && selectedIds.length === 0) onSelectedIds([currentUserId])
            }}
            className={`flex-1 py-2 text-xs font-semibold transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-casevo-mid ${i > 0 ? 'border-l border-casevo-border' : ''} ${audience === o.key ? 'bg-casevo-primary text-white' : 'bg-casevo-surface text-casevo-forest hover:bg-casevo-mint/30'}`}>
            {o.label}
          </button>
        ))}
      </div>
      {audience === 'firm' && (
        <p className="mt-1.5 text-[11px] text-casevo-mid">
          {noun === 'task' ? 'Everyone in the firm will see this task.' : 'Everyone in the firm will see this case.'}
        </p>
      )}
      {audience === 'people' && (
        <div className="mt-2">
          <label className={lblCls}>{noun === 'task' ? 'People' : 'Handling team'}</label>
          <div className="border border-casevo-border rounded-lg bg-casevo-input p-2 max-h-40 overflow-y-auto flex flex-col gap-0.5">
            {activeUsers.map((u) => {
              const checked = selectedIds.includes(u.id)
              const isYou = u.id === currentUserId
              return (
                <button key={u.id} type="button" aria-pressed={checked}
                  onClick={() => onSelectedIds(checked ? selectedIds.filter((id) => id !== u.id) : [...selectedIds, u.id])}
                  className="flex items-center gap-2.5 px-2 py-1.5 rounded-md hover:bg-casevo-mint/40 focus:outline-none focus-visible:bg-casevo-mint/40 text-left">
                  <span className={`w-[17px] h-[17px] rounded-[5px] border flex items-center justify-center shrink-0 ${checked ? 'bg-casevo-primary border-casevo-primary text-white' : 'border-casevo-border'}`}>{checked && <IcCheck />}</span>
                  <UserAvatar userId={u.id} fallbackName={`${u.firstName} ${u.lastName}`.trim() || u.email} size="xs" title={false} />
                  <span className="text-[13px] text-casevo-forest truncate">{nameOf(u)}</span>
                  {isYou && <span className="ml-auto text-[10.5px] text-casevo-mid shrink-0">you</span>}
                </button>
              )
            })}
            {activeUsers.length === 0 && <span className="text-xs text-casevo-mid px-2 py-1">No firm members to add.</span>}
          </div>
        </div>
      )}
    </div>
  )
}

export default AudienceControl
